import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';

export interface WorldCupTeam {
  name: string;
  logo: string | null;
  abbr: string | null;
}

export interface WorldCupTableMarketMeta {
  event_id: number | null;
  event_slug: string | null;
  total_volume: number | null;
  active: number | null;
  closed: number | null;
}

export interface WorldCupTableMarketRow {
  outcome: string;
  participant_type: string;
  total_volume?: number | null;
  polymarket: number | null;
  betmgm: number | null;
  fanduel: number | null;
  draftkings: number | null;

  betmgmWinRatePercent: number;
  fanduelWinRatePercent: number;
  draftkingsWinRatePercent: number;
}

export interface WorldCupBookmakerMarkets {
  meta: WorldCupTableMarketMeta;
  rows: WorldCupTableMarketRow[];
}

export interface WorldCupGamelinesData {
  slug: string;
  title: string;
  home_team: WorldCupTeam;
  away_team: WorldCupTeam;
  game_start_time: string;
  game_event_id: string | null;
  moneyline: WorldCupBookmakerMarkets;
  spreads: WorldCupBookmakerMarkets;
  totals: WorldCupBookmakerMarkets;
  both_teams_to_score: WorldCupBookmakerMarkets;
}

export interface WorldCupGamelinesResponse {
  success: boolean;
  data: WorldCupGamelinesData;
  message?: string;
}

export interface UseWorldCupGamelinesOptions {
  eventSlug?: string | null;
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupGamelinesResponse, Error>;
}

export async function fetchWorldCupGamelines(eventSlug: string) {
  const searchParams = new URLSearchParams({
    event_slug: eventSlug,
  });

  const response = await customFetch(`/api/world-cup/game/gamelines?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch World Cup gamelines');
  }

  const result = (await response.json()) as WorldCupGamelinesResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup gamelines');
  }

  return result;
}

export function useWorldCupGamelines(options: UseWorldCupGamelinesOptions = {}) {
  const eventSlug = options.eventSlug?.trim();
  const enabled = options.enabled ?? true;
  const swrKey = enabled && eventSlug ? ['world-cup-gamelines', eventSlug] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<WorldCupGamelinesResponse, Error>(
    swrKey,
    () => fetchWorldCupGamelines(eventSlug!),
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    gamelines: data?.data ?? null,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupGamelines;
